import { type MouseEvent, type ReactNode, type RefObject, useId } from 'react';
import { Overlay } from '@/shared/ui/overlay/overlay';
import './popup.css';

export interface PopupBoxContent {
  heading: ReactNode;
  description: ReactNode;
}

export interface PopupProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  box?: PopupBoxContent;
  footer?: ReactNode;
  children?: ReactNode;
  triggerRef?: RefObject<HTMLElement | null>;
  className?: string;
}

export function Popup({ open, onClose, title, description, box, footer, children, triggerRef, className }: PopupProps) {
  const titleId = useId();

  function handleScrimClick(event: MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) onClose();
  }

  return (
    <Overlay open={open} onClose={onClose} triggerRef={triggerRef} dismissOnOutsideClick={false}>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: 스크림은 dismiss 전용 배경이며 포커스 대상이 아니다 — 키보드 사용자는 overlay의 Esc 처리로 동일하게 닫는다 */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: 위와 동일 — 스크림은 tab 순서에 없어 키 이벤트가 의미 없다 */}
      <div className="popup" onClick={handleScrimClick}>
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className={['popup__content', className].filter(Boolean).join(' ')}
        >
          <h2 id={titleId} className="popup__title">
            {title}
          </h2>
          {description && <p className="popup__desc">{description}</p>}
          {box && (
            <div className="popup__box">
              <b>{box.heading}</b>
              <p>{box.description}</p>
            </div>
          )}
          {children}
          {footer && <div className="popup__footer">{footer}</div>}
        </div>
      </div>
    </Overlay>
  );
}
